/**
 * FRONT-OFFICE-OS-V2-BUILD-V1 -- the home global-search + register modal
 * (founder rulings FO-3/FO-4/FO-5, 2026-08-01; model:
 * global-search-model.ts).
 *
 * ONE search box on the front-desk home is the door; THIS centered modal
 * is the room (FO-4, Salesforce-style): candidate cards off the EXISTING
 * patient-master search wire (the shared-phone family list is a
 * first-class case, ADR-0013), pick the person -> the patient record.
 * Nothing found -> "No patients found" + exactly ONE action,
 * "Register patient", inside the same modal (FO-4 verbatim copy).
 *
 * The register view is founder ruling D7's person-only flow over the
 * EXISTING create contract (createPatientMasterPatient -- THE ONE writer;
 * its deterministic duplicate-first check stays binding): register the
 * person, land on the new patient record, create NO visit. It reuses THE
 * ONE register field set (PatientCreateContent, variant 'modal') -- one
 * presentation, three hosts, never a fork -- and opens ONLY behind search
 * evidence (globalRegisterEvidence -- the in-modal C13 structural gate).
 *
 * SECURITY: presentation only. The backend patient-master fences (flag
 * gate, role scope, org scope, dedupe ladder) are the boundary; candidate
 * cards carry the Tier-0 disambiguation fields ONLY (never a phone).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';

import {
  createPatientMasterPatient,
  recordWalkinArrival,
  fetchPatientMasterRecord,
  revealPatientContact,
  fetchDoctorPatientSearch,
  fetchPatientMasterSearch,
  openPatientSearchLead,
  fetchRegistrationConfig,
  postLeadBridge,
  type PatientConvertCandidateView,
} from '@/dashboard/api';
import {
  doctorLensHref,
  DOCTOR_SEARCH_NO_MATCH_BODY,
  DOCTOR_SEARCH_NO_MATCH_TITLE,
} from '@/dashboard/doctor/doctor-patient-lens-view';
import {useRegistrationOperation} from './use-registration-operation';
import { useDashboardMeContext } from '@/dashboard/me-context';
import RecentlyViewedPatientsStrip from '@/dashboard/patients/RecentlyViewedPatientsStrip';
import {
  AcBadge,
  AcButton,
  ModalShell,
  type AppointmentStatusLabelOverrides,
} from '@/dashboard/ui';
import { sourceLabel } from '@/walkin/WalkinSourceCorrectionDoor';
import { PatientCreateContent } from '@/dashboard/patients/PatientCreatePage';
import {
  buildBridgeContinuePayload,
  buildCreatePayload,
  createFormAddressDefaultsPatch,
  EMPTY_CREATE_FORM,
  ENQUIRY_CONTINUE_TRUST_LINE,
  ENQUIRY_GLANCE_TITLE,
  enquiryPhoneOnFileLine,
  missingRequiredCreateKeys,
  deskRecordHref,
  patientRecordHref,
  type DeskRecordOrigin,
  registrationConfigErrors,
  sharedPhoneCaption,
  validateBridgeContinueForm,
  validateCreateForm,
  WALKIN_SOURCE_OPTIONS,
  type CreateFormState,
  type PatientCandidateCard,
  type CreatePatientResponse,
  type EnquiryGlanceCard,
  type PatientSearchAppointmentMatch,
  type PatientSearchResponse,
} from '@/dashboard/patients/patient-master-view';
import { dayStatusChip } from '@/dashboard/appointments/ClinicDayBoard';
import { MID_DOT } from '@/dashboard/doctor/doctor-home-view';
import { formatTimestampIst } from '@/lib/datetime-ist';
import {
  ARRIVAL_NONE_OF_THESE,
  ARRIVAL_SEARCH_BUTTON,
  ARRIVAL_SEARCH_PLACEHOLDER,
  ARRIVAL_SEARCH_UNAVAILABLE,
  ARRIVAL_TITLE_SEARCH,
  FALLBACK_REGISTRATION_CONFIG,
  patientPlaneRequiredFields,
  registrationSectionsPrefill,
  resolvedRequiredFields,
  type RegistrationConfigView,
} from '@/dashboard/patients/register-wizard-model';
import {
  EnquiryCard,
  RecordCard,
  type SearchResultCardView,
} from '@/dashboard/patients/RegisterPatientSteps';
import { registrationNeedsLine } from '@/dashboard/appointments/registration-field-plan';
import { clinicToday } from '@/dashboard/ui/day-scope';
import { PortalApiError, portalApiPost } from '@/lib/portal-api';
import type { CreatePatientPayload } from '@/dashboard/patients/patient-master-view';

import RegisterBookingOffer, {
  BOOKING_OFFER_TITLE,
} from '@/dashboard/patients/RegisterBookingOffer';
import {
  GLOBAL_SEARCH_BACK_TO_SEARCH,
  GLOBAL_SEARCH_NO_PATIENTS,
  GLOBAL_SEARCH_REGISTER_ACTION,
  globalRegisterDraftKey,
  globalRegisterEvidence,
  parseGlobalRegisterDraft,
  registerFieldsFromQuery,
  serializeGlobalRegisterDraft,
  GLOBAL_REGISTER_DRAFT_VERSION,
  type GlobalSearchState,
} from '@/dashboard/patients/global-search-model';

interface RegistrationAttempt { requestId: string; payload: CreatePatientPayload; draft: string; source: string; sourceDetail?: string; reasonForVisit?: string; bridge?: { enquiry: EnquiryGlanceCard; payload: Parameters<typeof postLeadBridge>[0]; patientId?: string; patientIsNew?: boolean; reviewPersonRequired?: boolean } }

// ---- D2 (RECEPTION-WALK-CORRECTIVE-V1): the landed-day board href ----------

/**
 * The /walkin href scoped to the clinic-day a just-booked appointment LANDED
 * on. Reuses THE ONE IST clinic-day arithmetic (clinicToday, day-scope.ts) --
 * never a second timezone read -- to derive the landed day from the booked UTC
 * instant, and the day-scope URL law (today = the bare route, every other day
 * carries ?date). So the board opens on the day the row actually lives.
 *
 * SHIPPED-WORK-TRUTH-CORRECTIVE-V1 (T3, founder ruling 2026-08-15): an
 * optional appointment id rides the SAME href as `appointment=<id>` -- the
 * board then scrolls to and highlights THAT row (the exact-appointment
 * landing; a generic day landing is not acceptance). Existing callers pass
 * nothing and keep the prior href byte-identical.
 */
export function walkinBoardHref(bookedIso: string, appointmentId?: number): string {
  const landedDay = clinicToday(new Date(bookedIso));
  const base =
    landedDay === clinicToday() ? '/walkin' : `/walkin?date=${landedDay}`;
  if (appointmentId === undefined) return base;
  return `${base}${base.includes('?') ? '&' : '?'}appointment=${appointmentId}`;
}

// ---- D5 idiom: session-storage draft plumbing (fail-open reads) ------------

function readDraftRaw(key: string): string | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage.getItem(key) : null;
  } catch {
    return null;
  }
}
function writeDraftRaw(key: string, value: string): void {
  try {
    if (typeof window !== 'undefined') window.sessionStorage.setItem(key, value);
  } catch {
    /* a failed draft write loses nothing but resume convenience */
  }
}
function clearDraftRaw(key: string): void {
  try {
    if (typeof window !== 'undefined') window.sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** The authenticated-actor resolution the draft is KEYED to (the D5
 *  posture from the arrival wizard host: no session = no draft;
 *  fail-closed for privacy). */
async function resolveDraftActor(): Promise<string | null> {
  try {
    const mod = await import('@/lib/supabase');
    const client = mod.getSupabaseBrowserClient();
    if (!client) return null;
    const { data } = await client.auth.getSession();
    const id = data.session?.user?.id;
    return typeof id === 'string' && id !== '' ? id : null;
  } catch {
    return null;
  }
}

// ---- Pure presentational search body (SSR-pinnable) ------------------------

export interface GlobalSearchContentProps {
  /** Desk-only accelerator; undefined keeps doctor content structurally absent. */
  readonly recentOrigin?: DeskRecordOrigin | null;
  readonly query: string;
  readonly onQueryChange?: (q: string) => void;
  readonly onSearch?: () => void;
  readonly searching: boolean;
  /** Verbatim service-failure message; null = no failure. */
  readonly searchError: string | null;
  /** Null until the first search completes. */
  readonly result: PatientSearchResponse | null;
  readonly onPick?: (card: SearchResultCardView) => void;
  /** D4 (RECEPTION-WALK-CORRECTIVE-V1): book a visit for a search-found EXISTING
   *  patient (the walk-in "search existing -> book" flow). Absent = no booking
   *  affordance (the record-open path is unchanged). */
  readonly onBook?: (card: SearchResultCardView) => void;
  readonly onRegister?: () => void;
  /** The explicit operator override under results (its OWN evidence token,
   *  'none-of-these' -- the C13 gate needs to know which door was used). */
  readonly onNoneOfThese?: () => void;
  /** FD-16: continue the person's open enquiry as a registration. */
  readonly onContinueEnquiry?: (card: EnquiryGlanceCard) => void;
  /** PDC-FO-GAP-CLOSURE-V1 (G1): open the day board on the matched
   *  appointment's landed day (the walkinBoardHref door). Absent = the
   *  card renders without a door (never a dead button -- LES-016). */
  readonly onOpenAppointmentDay?: (match: PatientSearchAppointmentMatch) => void;
  /** The org's display-label overrides for stored appointment statuses
   *  (`appointment.status_labels` off /me) -- the ONE StatusChip canon
   *  consumes them; absent = canon defaults. */
  readonly statusLabels?: AppointmentStatusLabelOverrides | null;
  /** SHIPPED-WORK-TRUTH-CORRECTIVE-V1 (T2): the no-match copy for a context
   *  with NO register door (the doctor context). When `onRegister` is absent
   *  the empty state renders THIS title/body pair instead of the desk's
   *  register action (LES-016: a door renders only where its handler
   *  exists). Both absent + onRegister present = the desk block,
   *  byte-identical. */
  readonly noMatchTitle?: string;
  readonly noMatchBody?: string;
  readonly busy: boolean;
}

export function GlobalSearchContent({
  recentOrigin,
  query,
  onQueryChange,
  onSearch,
  searching,
  searchError,
  result,
  onPick,
  onBook,
  onRegister,
  onNoneOfThese,
  onContinueEnquiry,
  onOpenAppointmentDay,
  statusLabels,
  noMatchTitle,
  noMatchBody,
  busy,
}: GlobalSearchContentProps): JSX.Element {
  const cards: readonly SearchResultCardView[] =
    result === null
      ? []
      : result.candidates.map((c) => ({
          patientId: c.patientId,
          umr: c.umr,
          fullName: c.fullName,
          gender: c.gender,
          ageYears: c.ageYears,
          city: c.city,
          registeredClinicId: c.registeredClinicId,
          registeredClinicLabel: c.registeredClinicLabel,
        }));
  // FD-16 (tolerant reads, LES-057): both new wire fields degrade to
  // empty on an older/partial payload -- never a crash, never a fake row.
  const deskFacts = result?.deskFacts ?? {};
  const enquiries = result?.enquiries ?? [];
  // PDC-FO-GAP-CLOSURE-V1 (G1): the appointment an appointment-id-shaped
  // query resolved (tolerant read: absent on every other query shape).
  const appointmentMatch = result?.appointmentMatch ?? null;
  return (
    <div className="ac-wizard" data-testid="global-search-content">
      {recentOrigin !== undefined ? <RecentlyViewedPatientsStrip origin={recentOrigin} /> : null}
      <div className="ac-wizard__card ac-wizard__search-card">
        <input
          className="ac-wizard__search-input"
          type="search"
          placeholder={ARRIVAL_SEARCH_PLACEHOLDER}
          value={query}
          aria-label="Search patients by mobile, UMR, or name"
          onChange={(e) => onQueryChange?.(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSearch?.();
            }
          }}
          data-testid="global-search-input"
        />
        <AcButton
          size="sm"
          variant={result === null ? 'primary' : 'secondary'}
          disabled={busy || searching || query.trim() === ''}
          onClick={() => onSearch?.()}
          data-testid="global-search-button"
        >
          {searching ? 'Searching...' : ARRIVAL_SEARCH_BUTTON}
        </AcButton>
      </div>

      {searchError !== null ? (
        // Degraded mode (the #414 corrective's case d, one vocabulary): a
        // genuine search-service failure keeps registration available with
        // the honest warning -- the server's duplicate-first check remains
        // mandatory at submit. T2: the register door renders ONLY where a
        // register handler exists (the desk); the doctor context keeps the
        // honest warning alone (LES-016 -- never a dead door).
        <>
          <div
            className="ac-banner ac-banner--warn"
            role="status"
            data-testid="global-search-unavailable"
          >
            {ARRIVAL_SEARCH_UNAVAILABLE}
          </div>
          {onRegister !== undefined ? (
            <div className="ac-wizard__card ac-wizard__empty">
              <AcButton
                variant="primary"
                disabled={busy}
                onClick={() => onRegister()}
                data-testid="global-search-register"
              >
                {GLOBAL_SEARCH_REGISTER_ACTION}
              </AcButton>
            </div>
          ) : null}
        </>
      ) : null}

      {searching && result === null ? (
        <div className="ac-wizard__card ac-wizard__skeleton" aria-hidden="true">
          <span className="ac-wizard__skeleton-bar" style={{ width: '70%' }} />
          <span className="ac-wizard__skeleton-block" />
          <span className="ac-wizard__skeleton-block" />
        </div>
      ) : null}

      {appointmentMatch !== null ? (
        // PDC-FO-GAP-CLOSURE-V1 (G1): the SAME box resolved an appointment
        // number -- one card, admin facts only (who / when / stored-status
        // chip / doctor / clinic), and ONE door. SHIPPED-WORK-TRUTH-
        // CORRECTIVE-V1 (T3, founder ruling 2026-08-15): the door lands on
        // THE appointment -- walkinBoardHref carries the id and the board
        // scrolls to + highlights that row (a generic day landing is not
        // acceptance) -- and the card names the DOCTOR. The status renders
        // through the ONE StatusChip canon + org overrides.
        <div
          className="ac-wizard__card"
          data-testid="global-search-appointment-match"
        >
          <p className="ac-wizard__card-title">
            {`Appointment #${appointmentMatch.appointmentId}`}
          </p>
          <p className="ac-wizard__card-line">
            {appointmentMatch.patientLabel !== null ? (
              <span data-testid="appointment-match-patient">
                {appointmentMatch.patientLabel}
              </span>
            ) : null}
            <span data-testid="appointment-match-time">
              {(appointmentMatch.patientLabel !== null ? ` ${MID_DOT} ` : '') +
                formatTimestampIst(appointmentMatch.appointmentTimeIso)}
            </span>
            {(appointmentMatch.doctorLabel ?? null) !== null ? (
              <span data-testid="appointment-match-doctor">
                {` ${MID_DOT} ${appointmentMatch.doctorLabel}`}
              </span>
            ) : null}
            {appointmentMatch.clinicLabel !== null ? (
              <span>{` ${MID_DOT} ${appointmentMatch.clinicLabel}`}</span>
            ) : null}
          </p>
          <p className="ac-wizard__card-line">
            <AcBadge
              tone={dayStatusChip(appointmentMatch.status, statusLabels ?? null).tone}
              testId="appointment-match-status"
            >
              {dayStatusChip(appointmentMatch.status, statusLabels ?? null).label}
            </AcBadge>
          </p>
          {onOpenAppointmentDay !== undefined ? (
            <AcButton
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => onOpenAppointmentDay(appointmentMatch)}
              data-testid="appointment-match-open-day"
            >
              Open appointment
            </AcButton>
          ) : null}
        </div>
      ) : null}

      {result !== null && result.count > 0 ? (
        <>
          {result.searchMode === 'phone' && result.count > 1 ? (
            <p className="ac-wizard__foot-note" role="note" data-testid="global-search-family">
              {sharedPhoneCaption(result.count)}
            </p>
          ) : null}
          <ul className="ac-wizard__cards" data-testid="global-search-results">
            {cards.map((c) => (
              <RecordCard
                key={c.patientId}
                card={c}
                onSelect={(card) => onPick?.(card)}
                onBook={onBook !== undefined ? (card) => onBook(card) : undefined}
                busy={busy}
                facts={deskFacts[c.patientId] ?? null}
              />
            ))}
          </ul>
          {/* T2: the none-of-these override leads to REGISTER -- desk only.
              A context without the handler (the doctor) renders no quiet
              door (LES-016). */}
          {onNoneOfThese !== undefined ? (
            <div className="ac-wizard__quiet-row">
              <button
                type="button"
                className="ac-wizard__quiet"
                disabled={busy}
                onClick={() => onNoneOfThese()}
                data-testid="global-search-none-of-these"
              >
                {ARRIVAL_NONE_OF_THESE}
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {result !== null &&
      result.count === 0 &&
      searchError === null &&
      enquiries.length > 0 ? (
        // FD-16 (RECEPTION LAW 1): no patient, but the person has an OPEN
        // enquiry -- registration CONTINUES the journey (LAW 2). The
        // continue button is the primary; registering a different person
        // stays one quiet act below (never a dead end either way).
        <>
          <p className="ac-wizard__card-title" data-testid="enquiry-glance-title">
            {ENQUIRY_GLANCE_TITLE}
          </p>
          <ul className="ac-wizard__cards" data-testid="enquiry-glance-results">
            {enquiries.map((e) => (
              <EnquiryCard
                key={e.leadId}
                card={e}
                onContinue={(card) => onContinueEnquiry?.(card)}
                busy={busy}
              />
            ))}
          </ul>
          <div className="ac-wizard__quiet-row">
            <button
              type="button"
              className="ac-wizard__quiet"
              disabled={busy}
              onClick={() => onRegister?.()}
              data-testid="global-search-register"
            >
              Different person? {GLOBAL_SEARCH_REGISTER_ACTION}
            </button>
          </div>
        </>
      ) : null}

      {result !== null &&
      result.count === 0 &&
      searchError === null &&
      enquiries.length === 0 &&
      appointmentMatch === null ? (
        // FO-4: minimal copy + exactly ONE action, inside the same modal.
        // PDC-FO-GAP-CLOSURE-V1 (G1): a RESOLVED appointment match stands
        // down this block -- the query asked for an appointment and got it,
        // so "No patients found" would be false and a register door beside
        // a found appointment would be noise. An UNRESOLVED appointment-id
        // query (appointmentMatch null) keeps this block exactly as before.
        // T2 (SHIPPED-WORK-TRUTH-CORRECTIVE-V1): a context WITHOUT a
        // register handler (the doctor) renders the honest no-match copy
        // naming the front desk instead -- never a dead register door.
        <div className="ac-wizard__card ac-wizard__empty" data-testid="global-search-no-match">
          <p className="ac-wizard__card-title">
            {onRegister !== undefined
              ? GLOBAL_SEARCH_NO_PATIENTS
              : (noMatchTitle ?? GLOBAL_SEARCH_NO_PATIENTS)}
          </p>
          {onRegister !== undefined ? (
            <AcButton
              variant="primary"
              disabled={busy}
              onClick={() => onRegister()}
              data-testid="global-search-register"
            >
              {GLOBAL_SEARCH_REGISTER_ACTION}
            </AcButton>
          ) : noMatchBody !== undefined ? (
            <p
              className="ac-wizard__foot-note"
              data-testid="global-search-no-match-body"
            >
              {noMatchBody}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---- The stateful modal ----------------------------------------------------

export default function GlobalPatientSearchModal({
  initialQuery,
  bookingUmr,
  onClose,
  walkinClinicId = null,
  audience = 'desk',
  recordOrigin = null,
  registrationIntent = false,
  continueLeadId,
  initialSearchPage = 0,
}: {
  /** Explicit Book appointment from an already-selected quick view. */
  readonly bookingUmr?: string;
  readonly registrationIntent?: boolean;
  readonly continueLeadId?: number;
  readonly initialSearchPage?: number;
  readonly initialQuery: string;
  readonly onClose: () => void;
  /** RECEPTION-OS-BUILD-V1 (F-14): the active working clinic for the
   *  walk-in arrival record. null = the walk-in record is skipped (the
   *  person create is never blocked by it). */
  readonly walkinClinicId?: number | null;
  /** SHIPPED-WORK-TRUTH-CORRECTIVE-V1 (T2, founder ruling 2026-08-15):
   *  THE ONE search door, role-aware (REUSE-THE-DOOR -- never a second
   *  modal). 'desk' (default) = the front-desk experience, byte-identical
   *  to before this prop existed. 'doctor' = the SAME room over the
   *  DOCTOR-fenced wire (fetchDoctorPatientSearch -- requireDoctorWorkspaceScope,
   *  clinic-fenced, contact structurally absent; the patient-master fence
   *  is byte-untouched and its wire is NEVER called): a picked card opens
   *  the DOCTOR LENS route, and every desk capability -- register, booking,
   *  enquiry continuation, none-of-these, the appointment-id branch -- is
   *  structurally ABSENT (no handler, no door; LES-016). */
  readonly audience?: 'desk' | 'doctor';
  /** L-18: the DOOR the host is, stamped onto the desk record the modal opens
   *  so that record's Back returns to the host rather than to a search page
   *  the operator never visited. A modal never assumes where it is mounted;
   *  null (the default) leaves every href byte-identical. Ignored in doctor
   *  mode, which opens the doctor lens and carries its OWN L-09 origin. */
  readonly recordOrigin?: DeskRecordOrigin | null;
}): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const ctx = useDashboardMeContext();
  const doctorMode = audience === 'doctor';
  const {begin: beginOperation, check: assertOperation, isCurrent: operationCurrent} =
    useRegistrationOperation(JSON.stringify([walkinClinicId, audience, initialQuery, location.key]));

  const [view, setView] = useState<'search' | 'register'>('search');
  const [query, setQuery] = useState(initialQuery);
  // The query the LAST completed search actually answered (trimmed) --
  // distinct from `query`, the live search-box text, which can be edited
  // after a search runs but before it's resubmitted. The register prefill
  // and the draft's scoping key off THIS, never the box text, so the
  // Mobile field always matches the evidence the operator is looking at
  // (FRONTDESK-REGISTER-PREFILL-TRUTH-V1).
  const [searchedQuery, setSearchedQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [result, setResult] = useState<PatientSearchResponse | null>(null);
  const [noneOfThese, setNoneOfThese] = useState(false);
  // FD-16 (RECEPTION LAWS 1+2): the enquiry being CONTINUED as a
  // registration (null = plain register). The journey reattaches through
  // the bridge; the phone is copied server-side.
  const [continuing, setContinuing] = useState<EnquiryGlanceCard | null>(null);
  // The bridge's explicit person-confirmation step (this phone already
  // belongs to at least one patient; the operator says who this is --
  // never an auto-link).
  const [existingLinkReview, setExistingLinkReview] = useState(false);
  const [personChoices, setPersonChoices] = useState<
    readonly PatientConvertCandidateView[] | null
  >(null);
  // Honest fallback: the enquiry has no copyable phone -> the mobile field
  // returns and the submit becomes a plain create (the enquiry stays open).
  const [bridgeNote, setBridgeNote] = useState<string | null>(null);
  // FD-20 (RECEPTION LAW 3): registration succeeded -> the booking offer
  // step (next-available by default; future override; skip stands).
  const [registered, setRegistered] = useState<{
    originWalkinId: number | null;
    patientId: string;
    patientName: string;
    leadId: number | null;
    patientUmr: string | null;
    patientIsNew: boolean;
    arrivalSource: string;
    originalSource: string | null;
    clinicId: number | null;
  } | null>(null);
  const [arrivalBookingOpen, setArrivalBookingOpen] = useState(false);
  // D4 (RECEPTION-WALK-CORRECTIVE-V1): "book a visit for an EXISTING patient
  // found by search" -- the SAME booking offer, patient-keyed (no lead, no
  // fresh registration). Book -> the landed-day board; Skip/close -> the record.
  const [bookExisting, setBookExisting] = useState<{
    patientId: string;
    patientName: string;
    patientUmr: string | null;
  } | null>(null);
  const [bookedOfferIso, setBookedOfferIso] = useState<string | null>(null);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [bookingReadError, setBookingReadError] = useState(false);
  const [bookingReadRetry, setBookingReadRetry] = useState(0);
  useEffect(() => {
    if (!bookingUmr || doctorMode) return;
    let active = true;
    setBookExisting(null); setBookingReadError(false);
    if (walkinClinicId === null) { setBookingReadError(true); return; }
    // Reuse exact scoped search to resolve the board's authoritative UMR.
    // Opening this form never invokes a booking or arrival writer.
    void fetchPatientMasterSearch(bookingUmr, 'umr', {clinicId: walkinClinicId, page: 0}).then(result => {
      const matches = result.candidates.filter(person => person.umr.toUpperCase() === bookingUmr.toUpperCase() && person.registeredClinicId === walkinClinicId);
      if (matches.length !== 1) throw new Error('Booking patient is unavailable');
      if (active) setBookExisting({patientId:matches[0]!.patientId,patientName:matches[0]!.fullName,patientUmr:matches[0]!.umr});
    }).catch(() => { if (active) setBookingReadError(true); });
    return () => { active = false; };
  }, [bookingUmr,doctorMode,walkinClinicId,bookingReadRetry]);


  const closeBookingOffer = (patientId: string): void => {
    if (bookingBusy) return;
    onClose();
    if (bookedOfferIso !== null) {
      navigate(walkinBoardHref(bookedOfferIso));
    } else if (recordOrigin !== 'walkin' && recordOrigin !== 'reports') {
      navigate(patientRecordHref(patientId));
    }
  };

  const [searchVersion, setSearchVersion] = useState(0);
  const searchGeneration = useRef(0);
  useEffect(() => () => { searchGeneration.current++; }, []);
  const runSearch = useCallback(
    (q: string): void => {
      const trimmed = q.trim();
      if (trimmed.length < 2) return;
      const serial = ++searchGeneration.current;
      setSearchVersion(value => value + 1);
      attemptKey.current = null; attemptRef.current = null;
      setAttempt(null); setAttemptReady(false); setActor(null);
      intentConsumed.current = false;
      setSearching(true);
      setSearchError(null);
      setNoneOfThese(false);
      setContinuing(null);
      setPersonChoices(null);
      setBridgeNote(null);
      setSearchedQuery(trimmed);
      // T2: the doctor context searches through the DOCTOR-fenced wire ONLY
      // (THE ONE search core server-side; contact structurally absent). The
      // desk wire is never called in doctor mode -- pinned. The doctor
      // response maps onto the same render shape with the desk-only fields
      // empty (no desk facts, no enquiry glance, no appointment branch).
      const search: Promise<PatientSearchResponse> = doctorMode
        ? fetchDoctorPatientSearch(trimmed).then((res) => ({
            searchMode: res.searchMode,
            count: res.count,
            candidates: res.candidates,
            deskFacts: {},
            enquiries: [],
          }))
        : fetchPatientMasterSearch(trimmed, undefined, registrationIntent && walkinClinicId !== null ? {clinicId:walkinClinicId,page:initialSearchPage} : undefined);
      search
        .then((res) => {
          if (serial !== searchGeneration.current) return;
          setSearching(false);
          setResult(res);
        })
        .catch((err: unknown) => {
          if (serial !== searchGeneration.current) return;
          setSearching(false);
          setResult(null);
          setSearchError(err instanceof Error ? err.message : 'Search failed');
        });
    },
    [doctorMode, registrationIntent, walkinClinicId, initialSearchPage],
  );

  // The hero's submitted query runs once on open (the modal opens AS the
  // answer surface); an empty open waits for the operator to type.
  useEffect(() => {
    if (bookingUmr) return;
    // Replay after StrictMode cleanup; the generation gate discards the first read.
    if (initialQuery.trim().length >= 2) runSearch(initialQuery);
  }, [initialQuery, runSearch, bookingUmr]);

  const searchState: GlobalSearchState = {
    searched: result !== null,
    resultCount: result?.count ?? null,
    searchFailed: searchError !== null,
    noneOfThese,
  };
  const evidence = globalRegisterEvidence(searchState);

  // ---- Register view state (D7 person-only over THE ONE writer) ----------
  const [form, setForm] = useState<CreateFormState>(EMPTY_CREATE_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [duplicate, setDuplicate] = useState<Extract<
    CreatePatientResponse,
    { outcome: 'duplicate_match' }
  > | null>(null);
  const [orgConfig, setOrgConfig] = useState<RegistrationConfigView>(
    FALLBACK_REGISTRATION_CONFIG,
  );
  const registeredClinicId =
    ctx?.me?.clinicIds.length === 1 ? ctx.me.clinicIds[0] ?? null : null;

  const [actor, setActor] = useState<string | null>(null);
  // Same actor-keyed, immutable sent-intent posture as PatientBookingFields.
  const [attempt, setAttempt] = useState<RegistrationAttempt | null>(null);
  const attemptRef = useRef<RegistrationAttempt | null>(null);
  const attemptKey = useRef<string | null>(null);
  const submitLock = useRef(false);
  const [attemptReady, setAttemptReady] = useState(false);
  // F-14: the walk-in source answer (the desk's own capture field, T-05).
  const [walkinSource, setWalkinSource] = useState('');
  const [referralName, setReferralName] = useState('');
  const [reasonForVisit, setReasonForVisit] = useState('');

  useEffect(() => {
    if (view !== 'register') return;
    let cancelled = false;
    fetchRegistrationConfig()
      .then((c) => {
        if (!cancelled) {
          setOrgConfig(c);
          // REGISTRATION-FIELD-CONTROLS-V1: State/Country open PREFILLED
          // from the config defaults (contract section 9), still-empty
          // fields only -- typed or draft-restored values are never
          // overwritten.
          if (!attemptRef.current) setForm((prev) => ({ ...prev, ...createFormAddressDefaultsPatch(prev, c) }));
        }
      })
      .catch(() => {
        /* the LAW-trio fallback stands -- a config fault never blocks */
      });
    return () => {
      cancelled = true;
    };
  }, [view]);

  // D5: bind the draft to the authenticated actor AND the search it was
  // captured under, then restore once. A draft whose query does not match
  // the CURRENT search belongs to a different patient-context (an
  // abandoned prior attempt) and is discarded WHOLE -- never merged
  // field-by-field -- so no residue (not mobile, not gender/dob/city) can
  // survive into an unrelated search. Esc/outside-click resume for the
  // SAME search (PR #414 DRAFT PRIVACY) still works: the draft's query
  // matches, so the whole form restores (FRONTDESK-REGISTER-PREFILL-TRUTH-V1
  // -- the ghost-mobile fix).
  useEffect(() => {
    if (searchedQuery.trim() === '') return;
    let cancelled = false;
    setAttemptReady(false);
    const expectedQuery = searchedQuery.trim();
    void resolveDraftActor().then((resolved) => {
      if (cancelled || resolved === null) return;
      setActor(resolved);
      const draft = parseGlobalRegisterDraft(
        readDraftRaw(globalRegisterDraftKey()),
        resolved,
      );
      if (draft !== null && draft.query === expectedQuery) {
        setForm({ ...draft.form });
      }
      const key = `patient-registration:v1:${resolved}:${registeredClinicId}:${expectedQuery}`;
      try {
        const raw = sessionStorage.getItem(key);
        if (raw !== null) {
          const previous = JSON.parse(raw) as RegistrationAttempt;
          const kept = parseGlobalRegisterDraft(previous.draft, resolved);
          if (!kept || kept.query !== expectedQuery || !previous.payload ||
            !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(previous.requestId) ||
            typeof previous.payload.fullName !== 'string' || typeof previous.payload.mobile !== 'string' ||
            typeof previous.source !== 'string') throw new Error('Invalid saved request');
          attemptRef.current = previous; setAttempt(previous);
          if (previous.bridge) setContinuing(previous.bridge.enquiry);
          if (previous.bridge?.reviewPersonRequired) setSaveError('The saved patient link changed. Refresh the search and review the person before recording arrival. Your entered details are kept.');
          setForm({...kept.form}); setWalkinSource(previous.source); setReferralName(previous.sourceDetail ?? ''); setReasonForVisit(previous.reasonForVisit ?? ''); setView('register');
        }
        attemptKey.current = key; setAttemptReady(true);
      } catch {
        setSaveError('The saved registration request could not be read. Check the patient record before registering again.');
      }
    });
    return () => { cancelled = true; };
  }, [searchVersion, searchedQuery, registeredClinicId]);

  // D5: persist on every change once an actor is bound (session-only) --
  // the query it was captured under rides along so a later restore can
  // prove the draft still belongs to the search in front of the operator.
  useEffect(() => {
    if (view !== 'register' || actor === null) return;
    writeDraftRaw(
      globalRegisterDraftKey(),
      serializeGlobalRegisterDraft({
        version: GLOBAL_REGISTER_DRAFT_VERSION,
        actor,
        query: searchedQuery.trim(),
        form,
      }),
    );
  }, [view, actor, form, searchedQuery]);

  // Person-only door: gate + render ONLY the patients-plane-persistable contract
  // (attack-review Finding 1 -- never force a field the writer drops).
  const required = useMemo(
    () => patientPlaneRequiredFields(resolvedRequiredFields(orgConfig)),
    [orgConfig],
  );
  const nowMs = Date.now();
  // FD-16: bridge mode = continuing an enquiry with a copyable phone -- the
  // mobile pair is on file server-side, so its checks drop (UX LAW 1); a
  // phone_unavailable fallback (bridgeNote set) restores the plain-create
  // rules including mobile.
  const bridgeMode = continuing !== null && bridgeNote === null;
  const validation = useMemo(
    () =>
      bridgeMode
        ? validateBridgeContinueForm(form, nowMs)
        : validateCreateForm(form, nowMs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, bridgeMode],
  );
  const configErrors = useMemo(() => {
    const errors = { ...registrationConfigErrors(form, required) };
    if (bridgeMode) delete errors.mobile;
    return errors;
  }, [required, form, bridgeMode]);
  const allErrors = useMemo(
    () => ({ ...configErrors, ...validation.errors }),
    [configErrors, validation.errors],
  );
  // REGISTRATION-DEPLOYED-WALK-CLOSURE-V1 (Defect C): gate on the FULL
  // org-required contract (parity with the check-in door), so neither the plain
  // register nor the continue-enquiry path can submit missing the Part C/D set.
  const missingKeys = useMemo(
    () => missingRequiredCreateKeys(form, required),
    [form, required],
  );
  const formOk = validation.ok && missingKeys.length === 0;


  const openRegister = (fromNoneOfThese = false): void => {
    if (registrationIntent && (searching || searchError !== null || result === null)) return;
    // The C13 gate, evaluated against the state INCLUDING the door being
    // pressed right now (the explicit override sets its evidence token in
    // the same act it opens the view -- no async-state gap).
    const nextEvidence = globalRegisterEvidence({
      ...searchState,
      noneOfThese: fromNoneOfThese || noneOfThese,
    });
    if (nextEvidence === null) return;
    if (fromNoneOfThese) setNoneOfThese(true);
    // A plain register never continues an enquiry (the quiet
    // different-person door under the glance lands here too).
    setContinuing(null);
    setPersonChoices(null);
    setBridgeNote(null);
    setDuplicate(null);
    setSaveError(null);
    setShowErrors(false);
    setWalkinSource(''); setReferralName(''); setReasonForVisit('');
    // TRUTH FIX (FRONTDESK-REGISTER-PREFILL-TRUTH-V1): reset to the EMPTY
    // defaults first, THEN apply only what the ACTUALLY-SEARCHED query
    // supplies -- never spread the prior form. No field from a prior
    // patient or a prior draft can survive; an empty search leaves every
    // field empty (never inherited).
    const savedDraft = actor === null ? null : parseGlobalRegisterDraft(
      attemptRef.current?.draft ?? readDraftRaw(globalRegisterDraftKey()), actor,
    );
    setForm(savedDraft?.query === searchedQuery.trim()
      ? { ...savedDraft.form }
      : { ...EMPTY_CREATE_FORM, ...registerFieldsFromQuery(searchedQuery) });
    if (attemptRef.current) setWalkinSource(attemptRef.current.source);
    setView('register');
  };

  // FD-16/FD-27: continue the person's open enquiry -- PRE-FILLED from the
  // enquiry (name, city) and from the typed query (mobile, for the
  // fallback path only; bridge mode never sends it). The C13 evidence is
  // the zero-result search that surfaced the enquiry.
  const openContinueEnquiry = (picked: EnquiryGlanceCard): void => {
    const serial = searchGeneration.current;
    void (async () => {
      try {
        if (walkinClinicId === null) throw new Error('Choose a clinic before continuing.');
        const bearer = await beginOperation();
        const card = await openPatientSearchLead(searchedQuery, picked.leadId, walkinClinicId, initialSearchPage);
        await assertOperation(bearer);
        if (serial !== searchGeneration.current) return;
        setContinuing(card);
    setPersonChoices(null);
    setBridgeNote(null);
    setDuplicate(null);
    setSaveError(null);
    setShowErrors(false);
    setWalkinSource(card.source ?? ''); setReferralName(''); setReasonForVisit('');
    // Same TRUTH FIX base as openRegister: EMPTY defaults first, then only
    // the enquiry's own name/city and the actually-searched mobile -- never
    // a spread of whatever a prior attempt left in `form`.
    const queryFields = registerFieldsFromQuery(searchedQuery);
    setForm({
      ...EMPTY_CREATE_FORM,
      ...(card.name !== null ? { fullName: card.name } : {}),
      ...(card.city !== null ? { city: card.city } : {}),
      ...(queryFields.mobile !== undefined ? { mobile: queryFields.mobile } : {}),
    });
    setView('register');
      } catch (error: unknown) {
        if (operationCurrent() && serial === searchGeneration.current) {
          intentConsumed.current = false;
          setSearchError('The enquiry could not be opened. Retry the search.');
        }
      }
    })();
  };

  const intentConsumed = useRef(false);
  const [reviewingSearch, setReviewingSearch] = useState(false);
  useEffect(() => {
    if (reviewingSearch || !registrationIntent || intentConsumed.current || searching || searchError || !result || !attemptReady) return;
    if (result.complete !== true) { setSearchError('Search is incomplete. Try again.'); return; }
    intentConsumed.current = true;
    // Recovery wins over a new search result: the enquiry may no longer be
    // unlinked after a committed bridge response. Keep its held writer/path.
    if (attemptRef.current) {
      setContinuing(attemptRef.current.bridge?.enquiry ?? null);
      setView('register');
      return;
    }
    const enquiry = continueLeadId === undefined ? undefined : result.enquiries?.find(item=>item.leadId===continueLeadId);
    if (continueLeadId !== undefined && !enquiry) { setSearchError('The enquiry is no longer available. Search again.'); return; }
    if (enquiry) openContinueEnquiry(enquiry); else openRegister(result.candidates.length > 0 || (result.enquiries?.length ?? 0) > 0);
  }, [reviewingSearch, registrationIntent, continueLeadId, searching, searchError, result, attemptReady]);

  /** The ONE post-register landing (create + bridge legs share it):
   *  confirmed walk-in arrival and audited patient link, draft cleared,
   *  then FD-20's BOOKING OFFER step (RECEPTION LAW 3: registration never
   *  dead-ends -- next-available offered; skip lands on the record). */
  const finishRegistered = async (
    patientId: string,
    patientIsNew: boolean,
    bearer: string,
    leadId: number | null = null,
    savedPerson?: PatientCandidateCard,
  ): Promise<void> => {
    await assertOperation(bearer);
    const walkinClinic = registeredClinicId ?? walkinClinicId;
    let originWalkinId: number | null = null;
    let confirmedPerson = savedPerson;
    if (walkinClinic !== null) {
      const savedAttempt = attemptRef.current;
      let arrivalName = form.fullName.trim(), arrivalPhone = form.mobile.trim();
      if (savedAttempt?.bridge) {
        const record = await fetchPatientMasterRecord(patientId, bearer);
        await assertOperation(bearer);
        confirmedPerson = record.patient;
        const contact = record.contactPoints.find(point=>point.type==='mobile' && point.hasValue);
        if (!contact) throw new Error('The saved patient has no usable mobile. Review the patient before recording arrival.');
        const revealed = await revealPatientContact(patientId,contact.id,'front_desk_checkin',bearer);
        await assertOperation(bearer);
        if (revealed.outcome !== 'revealed') throw new Error('The saved mobile is unavailable. Retry after reviewing the patient.');
        arrivalName = record.patient.fullName; arrivalPhone = revealed.value;
      }
      const arrival = await recordWalkinArrival({
        clinicId: walkinClinic,
        patientName: arrivalName,
        phoneRaw: arrivalPhone,
        source: savedAttempt?.source ?? walkinSource,
        ...(savedAttempt?.bridge ? {savedLeadId:savedAttempt.bridge.enquiry.leadId,patientId} : {}),
        ...((savedAttempt?.reasonForVisit ?? reasonForVisit.trim()) ? {reasonForVisit:savedAttempt?.reasonForVisit ?? reasonForVisit.trim()} : {}),
        ...(savedAttempt ? {requestId: savedAttempt.requestId} : {}),
        ...(savedAttempt?.sourceDetail ? {sourceDetail: savedAttempt.sourceDetail} : {}),
        patientIsNew,
      }, bearer);
      await assertOperation(bearer);
      if (!arrival?.ok || !Number.isSafeInteger(arrival.walkin_id)) {
        throw new Error('Arrival could not be confirmed. Retry the same registration.');
      }
      // Registration explicitly identifies this person. Reuse the audited CAS
      // link; a shared phone is never evidence for picking a family member.
      await portalApiPost(`/patient-master/walkins/${arrival.walkin_id}/link`, {patientId}, bearer);
      await assertOperation(bearer);
      originWalkinId = arrival.walkin_id;

    }

    setSubmitting(false);
    clearDraftRaw(globalRegisterDraftKey());
    if (walkinClinic !== null || leadId !== null) {
      // The offer step renders in-modal; its Skip lands on the record.
      setArrivalBookingOpen(false);
      setRegistered({
        originWalkinId,
        patientId,
        patientName: confirmedPerson?.fullName ?? form.fullName.trim(),
        patientUmr: confirmedPerson?.umr ?? null,
        patientIsNew,
        arrivalSource: attemptRef.current?.source ?? walkinSource,
        originalSource: attemptRef.current?.bridge?.enquiry.source ?? null,
        clinicId: walkinClinic,
        leadId,
      });
      return;
    }
    // No lawful clinic to offer slots for -- the record is the landing.
    onClose();
    navigate(patientRecordHref(patientId));
  };

  /** FD-16: one bridge call; every outcome lands honestly. `choice` rides
   *  the explicit person decision (link / create_new) when the operator
   *  has made one. */
  const runBridge = (
    choice:
      | { action: 'link'; patientId: string }
      | { action: 'create_new' }
      | null,
  ): void => {
    if (continuing === null || submitting || submitLock.current) return;
    if (!attemptReady || !attemptKey.current || !actor) {setSaveError('The registration session is not ready. Try again.');return;}
    const sent: RegistrationAttempt = attemptRef.current ?? {
      requestId:crypto.randomUUID(),payload:buildCreatePayload(form,validation.isMinor,registeredClinicId),
      draft:serializeGlobalRegisterDraft({version:GLOBAL_REGISTER_DRAFT_VERSION,actor,query:searchedQuery.trim(),form}),
      source:walkinSource,...(reasonForVisit.trim()?{reasonForVisit:reasonForVisit.trim()}:{}),
      bridge:{enquiry:continuing,payload:{...buildBridgeContinuePayload(form,validation.isMinor,continuing.leadId),
        ...(walkinClinicId !== null ? {clinicId: walkinClinicId} : {}),
        ...(choice?{patientChoice:choice}:{})}},
    };
    if (!sent.bridge) {setSaveError('Recover the saved registration before continuing another enquiry.');return;}
    const key=attemptKey.current;
    const retain=(value:RegistrationAttempt):void=>{sessionStorage.setItem(key,JSON.stringify(value));attemptRef.current=value;setAttempt(value);};
    const clear=():void=>{sessionStorage.removeItem(key);attemptRef.current=null;setAttempt(null);};
    try {retain(sent);} catch {setSaveError('This browser could not keep the registration request. Try again.');return;}
    if (choice !== null) setPersonChoices(null); // Keep unknown-result recovery on the saved, explicit choice.
    submitLock.current=true;setSubmitting(true);setSaveError(null);
    void (async()=>{
      const bearer = await beginOperation();
      const saved=sent.bridge!;
      if (!saved.patientId) {
        const res=await postLeadBridge(saved.payload,bearer);
        await assertOperation(bearer);
        if (res.outcome==='person_confirmation_required') {clear();setExistingLinkReview(res.existingLinkReview===true);setPersonChoices(res.candidates??[]);return;}
        if (res.outcome==='no_phone_on_lead' || res.outcome==='phone_unavailable') {
          clear();setBridgeNote('No usable mobile is on file for this enquiry. Enter the mobile to register this patient.');return;
        }
        if (!res.patientId) throw new Error('The saved patient could not be confirmed. Retry the saved request.');
        saved.patientId=res.patientId;saved.patientIsNew=res.outcome==='created_and_linked'||res.outcome==='created_not_linked';
        retain({...sent,bridge:saved});
      }
      setPersonChoices(null);
      await finishRegistered(saved.patientId!,saved.patientIsNew===true,bearer,saved.enquiry.leadId);
      await assertOperation(bearer);
      clear();
    })().catch((err:unknown)=>{
      if (!operationCurrent()) return;
      // A known rejected person choice cannot converge by replaying that choice.
      // Keep the draft and a durable review gate; unknown outcomes keep their
      // immutable request so a committed save can still be recovered safely.
      if (!sent.bridge?.patientId && err instanceof PortalApiError && err.status === 409 && err.code === 'lead_patient_choice_changed') {
        retain({...sent, bridge:{...sent.bridge!, reviewPersonRequired:true}});
        setSaveError('The saved patient link changed. Refresh the search and review the person before recording arrival. Your entered details are kept.');
        return;
      }
      setSaveError(sent.bridge?.patientId ? 'Patient saved. Arrival is not yet confirmed. Retry the saved request; your details are kept.' : err instanceof Error?err.message:'Registration outcome is not yet confirmed. Retry the saved request.');
    })
      .finally(()=>{if (operationCurrent()) {setSubmitting(false);submitLock.current=false;}});
  };

  const submitRegister = (): void => {
    if (attemptRef.current?.bridge?.reviewPersonRequired && !submitting && !submitLock.current) {
      // Explicitly leave the rejected writer and return to read-only selection.
      // Do not replace the selected patient or record arrival during recovery.
      if (attemptKey.current) sessionStorage.removeItem(attemptKey.current);
      attemptRef.current = null; setAttempt(null); setContinuing(null); setPersonChoices(null);
      setSaveError(null); setReviewingSearch(true); setView('search');
      runSearch(searchedQuery);
      return;
    }
    setShowErrors(true);
    setDuplicate(null);
    if (submitting || submitLock.current) return;
    if (!attemptRef.current && !form.contactReuse) {
      setSaveError('Choose Yes or No for reusing the emergency contact.');
      return;
    }
    if ((!formOk && attemptRef.current === null) || submitting || submitLock.current) return;
    if (!attemptRef.current && !continuing && !WALKIN_SOURCE_OPTIONS.some(option => option.value === walkinSource)) {
      setSaveError('Choose how the patient heard about us.');
      document.getElementById('walkin-source-select')?.focus();
      return;
    }
    if (!attemptRef.current && required.has('reason_for_visit') && !reasonForVisit.trim()) {
      setSaveError('Enter the reason for this visit.');
      return;
    }
    if (bridgeMode) {
      // Continue-from-enquiry: the bridge re-matches, links or creates via
      // THE ONE writer, and reattaches the journey (UMR mints exactly
      // once). No phone rides this request.
      runBridge(null);
      return;
    }
    if (!attemptReady || !attemptKey.current || !actor) {
      setSaveError('The registration session is not ready. Keep this window open and try again.');
      return;
    }
    const sent = attemptRef.current ?? {requestId: crypto.randomUUID(),
      payload: buildCreatePayload(form, validation.isMinor, registeredClinicId),
      draft: serializeGlobalRegisterDraft({version: GLOBAL_REGISTER_DRAFT_VERSION, actor, query: searchedQuery.trim(), form}),
      ...(reasonForVisit.trim() ? {reasonForVisit:reasonForVisit.trim()} : {}),
      source: walkinSource, ...(walkinSource === 'referral' && referralName.trim() ? {sourceDetail: referralName.trim()} : {})};
    try { sessionStorage.setItem(attemptKey.current, JSON.stringify(sent)); }
    catch { setSaveError('This browser could not keep the registration request. Keep this window open and try again.'); return; }
    const sentKey = attemptKey.current;
    submitLock.current = true; attemptRef.current = sent; setAttempt(sent);
    setSubmitting(true);
    setSaveError(null);
    // D7: the person is registered; NO visit is created -- the landing is
    // the patient record, where a visit starts explicitly when one is real.
    let patientSaveConfirmed = false;
    void (async () => {
        const bearer = await beginOperation();
        const res = await createPatientMasterPatient({...sent.payload, requestId: sent.requestId}, bearer);
        await assertOperation(bearer);
        patientSaveConfirmed = true;
        if (res.outcome === 'duplicate_match') {
          sessionStorage.removeItem(sentKey);
          attemptRef.current = null; setAttempt(null);
          setSubmitting(false);
          setDuplicate(res);
          return;
        }
        // F-14: record the walk-in arrival WITH its source through THE ONE
        // walk-in writer (session mode). Keep the request until both saves are confirmed.
        await finishRegistered(res.patient.patientId, true, bearer, null, res.patient);
        await assertOperation(bearer);
        sessionStorage.removeItem(sentKey);
        attemptRef.current = null; setAttempt(null);
      })()
      .catch((err: unknown) => {
        if (!operationCurrent()) return;
        setSubmitting(false);
        // The writer only returns validation 400 after either no admission or
        // a confirmed compensating delete. Unknown/auth/conflict keeps the key.
        if (!patientSaveConfirmed && err instanceof PortalApiError && err.status === 400) {
          sessionStorage.removeItem(sentKey); attemptRef.current = null; setAttempt(null);
        }
        setSaveError(patientSaveConfirmed ? 'Patient saved. Arrival is not yet confirmed. Retry the saved request; your details are kept.' :
          err instanceof PortalApiError && err.status < 500 ? err.message :
          'Registration outcome is not yet confirmed. Retry the saved request; your details are kept.');
      }).finally(() => { if (operationCurrent()) submitLock.current = false; });
  };

  const pick = (card: SearchResultCardView): void => {
    // T2: the doctor's pick opens the DOCTOR LENS (the doctor-shaped
    // Patient 360), never the Patient Master record route.
    onClose();
    navigate(
      doctorMode
        ? doctorLensHref(card.patientId)
        : deskRecordHref(card.patientId, recordOrigin, location.search),
    );
  };

  // Frozen Arrival: the confirmed save has its own outcome. Opening booking
  // is an explicit next action over the same saved patient and arrival.
  if (registered?.originWalkinId != null && !arrivalBookingOpen) {
    return <ModalShell size="lg" trapFocus title="Arrival recorded"
      description={registered.patientIsNew ? "New patient · Walk-in" : "Registered patient · Walk-in"} titleIcon={<Plus size={20}/>}
      testId="arrival-recorded-modal" onClose={() => closeBookingOffer(registered.patientId)}
      footer={<>
        <AcButton onClick={() => {setRegistered(null); setReviewingSearch(true); setView('search');}}>Back to search</AcButton>
        <AcButton variant="primary" disabled>Register patient &amp; record arrival</AcButton>
      </>}>
      <div className="arrival-saved-summary" role="status">
        <h3>Arrival recorded</h3>
        <p>{registered.patientName}{registered.patientUmr ? ' · ' + registered.patientUmr : ''}</p>
        <p>Appointment not booked.</p>
      </div>
      <dl className="arrival-saved-facts">
        <div><dt>Original enquiry source</dt><dd>{registered.originalSource ? sourceLabel(registered.originalSource) : 'No enquiry linked'}</dd></div>
        <div><dt>Arrival source</dt><dd>{sourceLabel(registered.arrivalSource)}</dd></div>
        <div><dt>Clinic</dt><dd>{ctx?.me?.clinicOptions?.find(clinic => clinic.clinicId === registered.clinicId)?.clinicLabel ?? 'Clinic ' + registered.clinicId}</dd></div>
      </dl>
      <div className="arrival-saved-actions">
        <AcButton variant="primary" onClick={() => setArrivalBookingOpen(true)}>Book appointment</AcButton>
        <AcButton onClick={() => {onClose(); navigate(patientRecordHref(registered.patientId));}}>Skip to record</AcButton>
        <AcButton onClick={() => {onClose(); navigate('/walkin');}}>Today’s patients</AcButton>
      </div>
    </ModalShell>;
  }

  // FD-20 (RECEPTION LAW 3): the post-register BOOKING OFFER -- the modal's
  // terminal step. Book -> the day board (the new row lives there);
  // Skip -> the patient record (the D7 landing). Never a dead end.
  if (registered !== null) {
    return (
      <ModalShell workspace
        title={bookedOfferIso !== null ? 'Appointment booked' : registered.originWalkinId != null ? 'Book appointment' : BOOKING_OFFER_TITLE}
        onClose={() => closeBookingOffer(registered.patientId)}
        testId="register-booking-offer-modal"
        closeDisabled={bookingBusy}
        trapFocus
      >
        <RegisterBookingOffer
          patientBookingDetails={!doctorMode}
          originWalkinId={registered.originWalkinId}
          onBusyChange={setBookingBusy}
          patientName={registered.patientName}
          patientId={registered.patientId}
          leadId={registered.originWalkinId != null ? null : registered.leadId}
          onBookingSaved={setBookedOfferIso}
          clinicId={registeredClinicId ?? walkinClinicId}
          onBooked={(appointmentId, bookedIso) => {
            // D2 (RECEPTION-WALK-CORRECTIVE-V1): jump the board to the LANDED
            // clinic-day, not just "/walkin" (which is always today). A booking
            // made late in the US day can land on the NEXT IST clinic-day; the
            // board must open on the day the row actually lives, or the row is
            // invisible and the board "gives zero indication" (the Aug-12 demo).
            onClose();
            navigate(walkinBoardHref(bookedIso, appointmentId));
          }}
          onSkip={() => {
            onClose();
            navigate(patientRecordHref(registered.patientId));
          }}
        />
      </ModalShell>
    );
  }

  // D4 (RECEPTION-WALK-CORRECTIVE-V1): the EXISTING-PATIENT booking offer --
  // reached from a search result's "Book appointment" (never a dead-end record
  // open). Reuses the SAME RegisterBookingOffer patient-keyed leg + the D2
  // landed-date confirmation + landed-day jump; leadId null (no enquiry leg).
  // Book -> the landed-day board; Skip/close -> that patient's record.
  if (bookExisting !== null) {
    return (
      <ModalShell workspace
        title={bookedOfferIso !== null ? 'Appointment booked' : 'Book appointment'}
        onClose={() => closeBookingOffer(bookExisting.patientId)}
        testId="register-booking-offer-modal"
        closeDisabled={bookingBusy}
        trapFocus
      >
        <RegisterBookingOffer
          patientBookingDetails={!doctorMode}
          onBusyChange={setBookingBusy}
          bookingIntent="appointment"
          bookingPresentation={!doctorMode}
          patientUmr={bookExisting.patientUmr}
          patientName={bookExisting.patientName}
          headline={bookExisting.patientName}
          patientId={bookExisting.patientId}
          leadId={null}
          onBookingSaved={setBookedOfferIso}
          clinicId={registeredClinicId ?? walkinClinicId}
          onBooked={(appointmentId, bookedIso) => {
            onClose();
            navigate(walkinBoardHref(bookedIso, appointmentId));
          }}
          onSkip={() => {
            onClose();
            navigate(patientRecordHref(bookExisting.patientId));
          }}
        />
      </ModalShell>
    );
  }

  if (bookingUmr && !doctorMode) return <ModalShell workspace title="Book appointment" onClose={onClose} testId="register-booking-offer-modal" trapFocus>
    {bookingReadError ? <><p role="alert">Patient could not be verified for booking. No appointment was saved.</p><AcButton onClick={() => setBookingReadRetry(n => n + 1)}>Try again</AcButton></> : <p role="status">Loading patient…</p>}
  </ModalShell>;

  // FD-16: the bridge's explicit person-confirmation step -- the searched
  // phone already belongs to patient(s); the operator names the person or
  // says "different person". Nothing is attached or created without it.
  if (view === 'register' && personChoices !== null && continuing !== null) {
    return (
      <ModalShell
        title="Who is this?"
        onClose={onClose}
        testId="global-register-confirm-person"
        closeDisabled={submitting}
        footer={
          <>
            <AcButton
              size="sm"
              variant="ghost"
              disabled={submitting}
              onClick={() => {setPersonChoices(null);if(existingLinkReview){setReviewingSearch(true);setView('search');runSearch(searchedQuery);}}}
              data-testid="confirm-person-back"
            >
              {existingLinkReview ? 'Back to search' : 'Back'}
            </AcButton>
            <span className="money-desk__spacer" />
            {!existingLinkReview ? (
            <AcButton
              size="sm"
              variant="secondary"
              disabled={submitting}
              onClick={() => runBridge({ action: 'create_new' })}
              data-testid="confirm-person-create-new"
            >
              Different person - register new
            </AcButton>
            ) : null}
          </>
        }
      >
        <p className="ac-wizard__foot-note" data-testid="confirm-person-note">
          {existingLinkReview ? 'This enquiry is already linked to the saved patient below. Confirm this person before recording arrival, or return to search.' : 'This mobile number already belongs to the patient record(s) below. Pick the person this enquiry is about, or register a new patient.'}
        </p>
        <ul className="ac-wizard__cards">
          {personChoices.map((c) => (
            <li
              key={c.patientId}
              className="ac-record-card"
              data-testid={`confirm-person-${c.patientId}`}
            >
              <dl className="ac-record-card__facts">
                <div className="ac-record-card__fact">
                  <dt>Name</dt>
                  <dd>{c.fullName}</dd>
                </div>
                <div className="ac-record-card__fact">
                  <dt>ID</dt>
                  <dd className="ac-record-card__num">{c.umr}</dd>
                </div>
                {typeof c.ageYears === 'number' ? (
                  <div className="ac-record-card__fact">
                    <dt>Age</dt>
                    <dd className="ac-record-card__num">{c.ageYears}</dd>
                  </div>
                ) : null}
                {c.city !== null && c.city !== '' ? (
                  <div className="ac-record-card__fact">
                    <dt>City</dt>
                    <dd>{c.city}</dd>
                  </div>
                ) : null}
              </dl>
              <AcButton
                size="sm"
                variant="primary"
                disabled={submitting}
                onClick={() =>
                  runBridge({ action: 'link', patientId: c.patientId })
                }
                data-testid={`confirm-person-link-${c.patientId}`}
              >
                This is the person
              </AcButton>
            </li>
          ))}
        </ul>
      </ModalShell>
    );
  }

  if (view === 'register' && (evidence !== null || attempt !== null || continuing !== null)) {
    return (
      <ModalShell size="lg" trapFocus
        title="Register patient"
        description="New patient · Walk-in"
        titleIcon={<Plus size={20}/>}
        onClose={onClose}
        testId="global-register-modal"
        closeDisabled={submitting}
        footer={
          <>
            <AcButton
              size="sm"
              variant="ghost"
              disabled={submitting}
              onClick={() => {setReviewingSearch(true); setView('search');}}
              data-testid="global-register-back"
            >
              {GLOBAL_SEARCH_BACK_TO_SEARCH}
            </AcButton>
            <span className="money-desk__spacer" />
            <AcButton
              variant="primary"
              disabled={submitting}
              onClick={submitRegister}
              data-testid="global-register-submit"
            >
              {submitting ? 'Saving...' : attempt?.bridge?.reviewPersonRequired ? 'Refresh & review person' : attempt ? 'Retry saved registration' : 'Register patient & record arrival'}
            </AcButton>
          </>
        }
      >
        {continuing !== null ? (
          // FD-27: the trust line -- the desk KNOWS the journey continues.
          <p
            className="ac-banner ac-banner--info"
            role="status"
            data-testid="enquiry-continue-trust-line"
          >
            {ENQUIRY_CONTINUE_TRUST_LINE}
          </p>
        ) : null}
        {continuing?.treatmentInterest ? <p className="ac-wizard__hint" data-testid="enquiry-interest">Enquiry interest: {continuing.treatmentInterest}. Confirm today's reason for visit below.</p> : null}
        {bridgeNote !== null ? (
          <p
            className="ac-banner ac-banner--warn"
            role="status"
            data-testid="enquiry-continue-fallback-note"
          >
            {bridgeNote}
          </p>
        ) : null}
        {evidence === 'search-failed' ? (
          <p
            className="ac-banner ac-banner--warn"
            role="status"
            data-testid="global-register-degraded-note"
          >
            {ARRIVAL_SEARCH_UNAVAILABLE}
          </p>
        ) : null}
        <fieldset disabled={submitting || attempt !== null} style={{border: 0, padding: 0, margin: 0, minWidth: 0}}>
        <PatientCreateContent
          visitFields={<>
        <label className="ac-wizard__field"><span className="ac-wizard__label">Reason for visit{required.has('reason_for_visit') ? ' *' : ''}</span>
          <input className="pim-input" value={reasonForVisit} onChange={e=>setReasonForVisit(e.target.value)} maxLength={200} aria-label="Reason for visit" disabled={submitting || attempt !== null}/>
        </label>
        <label className="ac-wizard__field" htmlFor="walkin-source-select">
          <span className="ac-wizard__label">How did they hear about us? *</span>
          <select
            className="pim-select"
            id="walkin-source-select"
            required={continuing === null}
            value={walkinSource}
            disabled={submitting || attempt !== null || continuing !== null}
            onChange={(e) => setWalkinSource(e.target.value)}
            data-testid="register-walkin-source"
          >
            <option value="">Choose a source</option>
            {continuing && walkinSource && !WALKIN_SOURCE_OPTIONS.some(option => option.value === walkinSource) ? <option value={walkinSource}>{sourceLabel(walkinSource)}</option> : null}
            {WALKIN_SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {walkinSource === 'referral' && continuing === null ? <label className="ac-wizard__field">
          <span className="ac-wizard__label">Referred by · if known</span>
          <input className="pim-input" value={referralName} disabled={submitting || attempt !== null} maxLength={200}
            onChange={event=>setReferralName(event.target.value)} data-testid="register-referral-name" />
        </label> : null}
          </>}
          variant="modal"
          workspaceRegistration
          handlingCategories={orgConfig.handlingCategories}
          sectionsPrefill={registrationSectionsPrefill(orgConfig)}
          form={form}
          onFieldChange={(field, value) =>
            setForm((prev) => ({ ...prev, [field]: value }))
          }
          onSubmit={submitRegister}
          submitting={submitting || attempt !== null}
          error={saveError}
          fieldErrors={showErrors ? allErrors : {}}
          isMinor={validation.isMinor}
          duplicate={duplicate}
          required={required}
          needsLine={
            showErrors && missingKeys.length > 0
              ? registrationNeedsLine(missingKeys)
              : null
          }
          mobileOnFile={
            bridgeMode ? enquiryPhoneOnFileLine(searchedQuery.trim()) : null
          }
        />
        </fieldset>
        <details className="arrival-new-privacy">
          <summary>Privacy &amp; communication</summary>
          <p>Registration does not grant treatment or marketing consent. Communication permissions stay separate.</p>
        </details>

      </ModalShell>
    );
  }

  // T2: the doctor context is the SAME room with the desk-only doors
  // structurally absent -- no register, no booking, no enquiry continuation,
  // no none-of-these override (each handler simply not passed; the content
  // renders no door without its handler, LES-016) -- and the honest doctor
  // no-match copy naming the front desk.
  if (registrationIntent && !reviewingSearch && view === 'search') return <ModalShell title="Register walk-in" onClose={onClose} trapFocus>
    {searchError ? <div role="alert"><p>Search could not be completed. No registration has been started.</p><AcButton onClick={()=>runSearch(initialQuery)}>Try again</AcButton></div> : <p role="status">Checking patient records…</p>}
  </ModalShell>;

  if (doctorMode) {
    return (
      <ModalShell
        title={ARRIVAL_TITLE_SEARCH}
        onClose={onClose}
        testId="global-search-modal"
      >
        <GlobalSearchContent
          query={query}
          onQueryChange={setQuery}
          onSearch={() => runSearch(query)}
          searching={searching}
          searchError={searchError}
          result={result}
          onPick={pick}
          statusLabels={ctx?.me?.appointmentStatusLabels ?? null}
          noMatchTitle={DOCTOR_SEARCH_NO_MATCH_TITLE}
          noMatchBody={DOCTOR_SEARCH_NO_MATCH_BODY}
          busy={false}
        />
      </ModalShell>
    );
  }

  return (
    <ModalShell
      title={ARRIVAL_TITLE_SEARCH}
      onClose={onClose}
      testId="global-search-modal"
    >
      <GlobalSearchContent
        recentOrigin={ctx?.me?.role === 'receptionist' ? recordOrigin : undefined}
        query={query}
        onQueryChange={setQuery}
        onSearch={() => runSearch(query)}
        searching={searching}
        searchError={searchError}
        result={result}
        onPick={pick}
        onBook={(card) =>
          setBookExisting({
            patientId: card.patientId,
            patientName: card.fullName,
            patientUmr: card.umr,
          })
        }
        onRegister={() => openRegister(false)}
        onNoneOfThese={() => openRegister(true)}
        onContinueEnquiry={openContinueEnquiry}
        onOpenAppointmentDay={(match) => {
          // PDC-FO-GAP-CLOSURE-V1 (G1): the day board on the appointment's
          // landed day -- THE ONE day-scope href derivation (walkinBoardHref;
          // today = the bare route). T3 (founder ruling 2026-08-15): the id
          // rides the href so the board opens ON the appointment (scroll +
          // highlight), never a generic day landing. Close first so the
          // board is what the desk sees, exactly like the booking-offer
          // landing.
          onClose();
          navigate(walkinBoardHref(match.appointmentTimeIso, match.appointmentId));
        }}
        statusLabels={ctx?.me?.appointmentStatusLabels ?? null}
        busy={submitting}
      />
    </ModalShell>
  );
}
